// Шторка агента с экрана заметки (M5).
//
// M5.1: контекст фиксирован и не выбирается руками. Путь — это открытая
// заметка, беседа — та, что записана в её капсуле контекста
// (`notesApi.context(path).sessionId`). Беседы нет — предлагаем «Отдать
// агенту» (`notesApi.handoff`), который создаёт её выжимкой контекста.
//
// M5.3: шторка не пишет в тело заметки ничего. Она умеет ровно одно —
// отправить реплику в сессию; правку агент оформит предложением, и та приедет
// в ленту обычным изменением файла. Отсюда и постоянная подпись под шапкой:
// человек должен видеть это до того, как продиктует правку, а не после.

import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiError, notesApi } from '../api'
import { SessionStream, deliverInput } from '../transport/ws'
import type { PermissionOptions } from '../transport/ws'
import { ChatThread } from './ChatScreen'
import DictationSheet from './DictationSheet'
import Sheet from './Sheet'
import './chat.css'

interface Props {
  path: string
  open: boolean
  onClose(): void
}

export default function AgentSheet({ path, open, onClose }: Props) {
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [handing, setHanding] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const [dictating, setDictating] = useState(false)

  const streamRef = useRef<SessionStream | null>(null)

  // Контекст перечитываем на каждое открытие: пока шторка была закрыта,
  // беседу могли создать с десктопа или отдать заметку агенту из ленты.
  useEffect(() => {
    if (!open) return
    let dead = false
    setLoading(true)
    setError(null)
    notesApi.context(path)
      .then((ctx) => {
        if (dead) return
        setSessionId(ctx.sessionId)
      })
      .catch((e: unknown) => {
        if (dead) return
        setSessionId(null)
        // 404 — капсулы у заметки ещё нет. Это не поломка: беседу можно
        // создать прямо отсюда, поэтому красной плашки не показываем.
        if (!(e instanceof ApiError && e.status === 404)) {
          setError(e instanceof Error ? e.message : 'не удалось прочитать контекст заметки')
        }
      })
      .finally(() => { if (!dead) setLoading(false) })
    return () => { dead = true }
  }, [open, path])

  // Стрим живёт только пока шторка открыта: держать сокет ради закрытой
  // панели незачем, а сессия на сервере от этого не страдает.
  useEffect(() => {
    if (!open || !sessionId) return
    const stream = new SessionStream(sessionId)
    streamRef.current = stream
    stream.start()
    return () => {
      stream.dispose()
      streamRef.current = null
    }
  }, [open, sessionId])

  const handoff = useCallback(async () => {
    if (handing) return
    setHanding(true)
    setError(null)
    try {
      const info = await notesApi.handoff(path)
      setSessionId(info.id)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'не удалось создать беседу')
    } finally {
      setHanding(false)
    }
  }, [handing, path])

  const submit = useCallback(() => {
    const text = draft.trim()
    if (!text || !sessionId) return
    setDraft('')
    deliverInput(sessionId, text)
  }, [draft, sessionId])

  const respond = useCallback((
    id: string, behavior: 'allow' | 'deny', opts?: PermissionOptions,
  ) => {
    streamRef.current?.respondPermission(id, behavior, opts)
  }, [])

  return (
    <Sheet open={open} title="Агент" onClose={onClose}>
      <div className="ab-asheet">
        <div className="ab-asheet__head">
          <span className={`ab-asheet__dot${sessionId ? ' is-on' : ''}`} aria-hidden />
          <span className="ab-asheet__titles">
            <span className="ab-asheet__sid">
              {sessionId ? `chat/${sessionId.slice(0, 8)}` : 'беседа не привязана'}
            </span>
            <span className="ab-asheet__ctx" title={path}>контекст: {path}</span>
          </span>
        </div>

        {/* M5.3 — обещание, которое шторка держит: в файл отсюда не пишется
            ничего, даже расшифровка диктовки. */}
        <div className="ab-asheet__vow">
          Правки в файл не пишутся: агент вернёт их предложением в ленту.
        </div>

        {error ? <div className="ab-asheet__fail">{error}</div> : null}

        {sessionId ? (
          <ChatThread sessionId={sessionId} onRespond={respond} />
        ) : (
          <div className="ab-asheet__none">
            {loading ? 'читаю контекст заметки…' : (
              <>
                <span className="ab-asheet__nonetext">
                  У этой заметки ещё нет беседы. Агент начнёт её с выжимки
                  контекста — что решено и что осталось открытым.
                </span>
                <button
                  type="button"
                  className="ab-asheet__give"
                  disabled={handing}
                  onClick={() => { void handoff() }}
                >
                  {handing ? 'создаю беседу…' : 'Отдать агенту'}
                </button>
              </>
            )}
          </div>
        )}

        <div className="ab-mcomposer ab-mcomposer--sheet">
          <input
            className="ab-mcomposer__field"
            value={draft}
            disabled={!sessionId}
            placeholder={sessionId ? 'что уточнить' : 'сперва отдайте заметку агенту'}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                e.preventDefault()
                submit()
              }
            }}
          />
          {draft.trim() ? (
            <button
              type="button"
              className="ab-mcomposer__send"
              aria-label="отправить"
              onClick={submit}
            >
              ↑
            </button>
          ) : (
            <button
              type="button"
              className="ab-mcomposer__mic"
              aria-label="надиктовать"
              title="надиктовать"
              disabled={!sessionId}
              onClick={() => setDictating(true)}
            >
              <span className="ab-mcomposer__micsq" aria-hidden />
            </button>
          )}
        </div>
      </div>

      <DictationSheet
        open={dictating}
        target="agent"
        sessionId={sessionId}
        onClose={() => setDictating(false)}
        onText={(text) => { if (sessionId) deliverInput(sessionId, text) }}
        autosend
      />
    </Sheet>
  )
}
